/* ================================================================
   Tournaments — Web MIDI + action registry
   Compact eigen versie, gescheiden localStorage-namespace van
   visionMidi/imagecompoMidi. Deelt de APC/LPD8 device profielen.
================================================================ */

export const ACTIONS = [
  { key: 'view.lobby',     label: 'View: Lobby',     group: 'View' },
  { key: 'view.bracket',   label: 'View: Bracket',   group: 'View' },
  { key: 'view.spotlight', label: 'View: Spotlight', group: 'View' },
  { key: 'view.live',      label: 'View: Live',      group: 'View' },
  { key: 'view.winner',    label: 'View: Winnaar',   group: 'View' },

  { key: 'spotlight.next', label: 'Spotlight: Volgende match', group: 'Spotlight' },
  { key: 'spotlight.prev', label: 'Spotlight: Vorige match',   group: 'Spotlight' },
]
export const ACTION_GROUPS = ['View','Spotlight']

/* ---- Web MIDI primitives ---- */
export async function openMIDI() {
  if (!navigator.requestMIDIAccess) throw new Error('Web MIDI API niet beschikbaar (Chrome/Edge vereist)')
  return navigator.requestMIDIAccess({ sysex: false })
}
export function listInputs(access)  { return Array.from(access.inputs.values()) }
export function listOutputs(access) { return Array.from(access.outputs.values()) }
export function attachInputListener(input, onEvent) {
  const handler = (msg) => {
    const [status, data1 = 0, data2 = 0] = msg.data
    const type = status & 0xF0
    const channel = status & 0x0F
    if (type === 0x80) return
    if (type === 0x90 && data2 === 0) return
    onEvent({ type, channel, data1, data2, raw: Array.from(msg.data) })
  }
  input.onmidimessage = handler
  return () => { input.onmidimessage = null }
}
export function eventKey(evt) {
  if (!evt) return null
  if (evt.type === 0x90) return `note:${evt.channel}:${evt.data1}`
  if (evt.type === 0xB0) return `cc:${evt.channel}:${evt.data1}`
  return null
}
export function eventLabel(evt) {
  if (!evt) return ''
  const t = evt.type
  if (t === 0x90) return `NOTE ${evt.data1} · ch${evt.channel + 1} · vel ${evt.data2}`
  if (t === 0xB0) return `CC ${evt.data1} · ch${evt.channel + 1} · val ${evt.data2}`
  return `0x${t.toString(16)} ${evt.data1} ${evt.data2}`
}
export function keyLabel(key) {
  if (!key) return ''
  const [kind, ch, num] = key.split(':')
  return `${kind.toUpperCase()} ${num} · ch${Number(ch) + 1}`
}
export function keyIsNote(key) { return typeof key === 'string' && key.startsWith('note:') }
export function keyToNoteNum(key) {
  const m = /^note:(\d+):(\d+)$/.exec(key || '')
  return m ? { channel: Number(m[1]), num: Number(m[2]) } : null
}

/* ---- LED (APC Mini mk1/mk2 + LPD8) ---- */
const _solid = [
  ['off','#333'],['white','#fff'],['red','#ff3838'],['orange','#ff9500'],['yellow','#ffcf40'],
  ['lime','#c8ff2b'],['green','#3adf3a'],['cyan','#00d9ff'],['blue','#337cff'],
  ['purple','#8b3fff'],['magenta','#ff2b8f'],['pink','#ff7ac8'],
]
export const LED_COLORS = Object.fromEntries([
  ..._solid.map(([k, sw]) => [k, { label: k, swatch: sw }]),
  ..._solid.filter(([k]) => k !== 'off').map(([k, sw]) => [`${k}_blink`, { label: `${k} (blink)`, swatch: sw }]),
])
export const LED_COLOR_KEYS = Object.keys(LED_COLORS)

const _mk2Idx = { white:3, red:5, orange:9, yellow:13, lime:17, green:21, cyan:33, blue:45, purple:49, magenta:53, pink:57 }
const _mk1Bucket = { green:['green','lime','cyan'], red:['red','orange','blue','purple','magenta','pink'], yellow:['yellow','white'] }
function _mk1Vel(c) {
  const solids = { green:1, red:3, yellow:5 }, blinks = { green:2, red:4, yellow:6 }
  const base = c.replace(/_blink$/,'')
  const b = Object.entries(_mk1Bucket).find(([, arr]) => arr.includes(base))?.[0] || 'red'
  return c.endsWith('_blink') ? blinks[b] : solids[b]
}
const _mk1Pal = { off: { ch:0, vel:0 } }
LED_COLOR_KEYS.forEach(k => { if (k !== 'off') _mk1Pal[k] = { ch: 0, vel: _mk1Vel(k) } })
const _mk2Pal = { off: { ch:0, vel:0 } }
for (const [n, i] of Object.entries(_mk2Idx)) { _mk2Pal[n] = { ch:6, vel:i }; _mk2Pal[`${n}_blink`] = { ch:14, vel:i } }

export const DEVICE_MODELS = {
  mk1:  { label: 'APC Mini (mk1) — 3 kleuren', hasLeds: true, palette: _mk1Pal },
  mk2:  { label: 'APC Mini mk2 — RGB',         hasLeds: true, palette: _mk2Pal },
  lpd8: { label: 'AKAI LPD8 — 8 pads + 8 knobs (geen LEDs)', hasLeds: false, palette: {} },
}
export const DEVICE_MODEL_KEYS = Object.keys(DEVICE_MODELS)
function getModel(n) { return DEVICE_MODELS[n] || DEVICE_MODELS.mk2 }
export function modelHasLeds(n) { return !!getModel(n).hasLeds }

export function sendLED(output, key, colorName, modelName) {
  if (!output || !keyIsNote(key)) return
  const model = getModel(modelName || loadModel()); if (!model.hasLeds) return
  const n = keyToNoteNum(key); if (!n) return
  const p = model.palette[colorName] || model.palette.off
  try { output.send([0x90 | (p.ch & 0x0F), n.num & 0x7F, p.vel & 0x7F]) } catch {}
}
export function clearAllLeds(output, modelName) {
  if (!output) return
  const model = getModel(modelName || loadModel()); if (!model.hasLeds) return
  for (let n = 0; n <= 89; n++) { try { output.send([0x90, n & 0x7F, 0]) } catch {} }
}

export function normalizeMapping(v) {
  if (typeof v === 'string') return { action: v, led_idle: 'off', led_active: 'off' }
  return { action: v?.action || '', led_idle: v?.led_idle || 'off', led_active: v?.led_active || 'off' }
}
export function evalActive(action, ctx = {}) {
  if (!action) return false
  if (action.startsWith('view.')) return ctx.state?.current_view === action.slice(5)
  return false
}
export function applyLeds(output, mappings, ctx, modelName) {
  if (!output || !mappings) return
  const model = modelName || loadModel()
  for (const [k, raw] of Object.entries(mappings)) {
    if (!keyIsNote(k)) continue
    const m = normalizeMapping(raw)
    const on = evalActive(m.action, ctx)
    sendLED(output, k, on ? m.led_active : m.led_idle, model)
  }
}

/* ---- Persistence ---- */
const MAP_KEY = 'tournaments-midi-mappings'
const IN_KEY  = 'tournaments-midi-input'
const OUT_KEY = 'tournaments-midi-output'
const MDL_KEY = 'tournaments-midi-model'
export function loadMappings() {
  try {
    const raw = JSON.parse(localStorage.getItem(MAP_KEY) || '{}'); const out = {}
    for (const [k, v] of Object.entries(raw)) out[k] = normalizeMapping(v)
    return out
  } catch { return {} }
}
export function saveMappings(m) { try { localStorage.setItem(MAP_KEY, JSON.stringify(m || {})) } catch {} }
export function loadInputId()  { try { return localStorage.getItem(IN_KEY)  || null } catch { return null } }
export function saveInputId(id){ try { id ? localStorage.setItem(IN_KEY, id) : localStorage.removeItem(IN_KEY) } catch {} }
export function loadOutputId() { try { return localStorage.getItem(OUT_KEY) || null } catch { return null } }
export function saveOutputId(id){try { id ? localStorage.setItem(OUT_KEY, id) : localStorage.removeItem(OUT_KEY) } catch {} }
export function loadModel()    { try { return localStorage.getItem(MDL_KEY) || 'mk2' } catch { return 'mk2' } }
export function saveModel(m)   { try { localStorage.setItem(MDL_KEY, m || 'mk2') } catch {} }
